import { format } from "date-fns";
import { StorageServiceInstance } from "../services/storage";
import { getSuiteName } from "./suite";
import {
  shortMonthDateFnsFormat,
  normalizeTeamRegion,
} from "./teamRegion";

type SupportRequestContextPayload = {
  pageHost: string | undefined;
  gameContext: string | undefined;
  seatContext: string | undefined;
};

/**
 * Captures fan app context when submitting support / feedback (hostname, game, seat).
 */
export function buildSupportRequestContext(): SupportRequestContextPayload {
  /** Includes port when present (e.g. localhost:3000 in dev). */
  const pageHost = typeof window !== "undefined" && window.location?.host ? window.location.host : undefined;

  const s = StorageServiceInstance;

  let gameContext: string | undefined;
  const g = s.selectedGameData;
  if (g?._id) {
    const t1 = g.team1?.name || "Home";
    const t2 = g.team2?.name || "Away";
    const parts: string[] = [`${t1} vs ${t2}`];
    if (g.startDate) {
      try {
        parts.push(format(
          new Date(g.startDate),
          shortMonthDateFnsFormat(normalizeTeamRegion(g.team1?.teamRegion)),
        ));
      } catch {
        /* ignore */
      }
    }
    if (g.stadium) {
      parts.push(g.stadium);
    }
    if (g.gameContext?.trim()) {
      parts.push(g.gameContext.trim());
    }
    parts.push(`id: ${g._id}`);
    gameContext = parts.join(" · ");
  } else if (s.selectedGame) {
    gameContext = `Game id: ${s.selectedGame}`;
  }

  let seatContext: string | undefined;
  if (s.selectedSectionIsSpecial && s.selectedSpecialSection) {
    const bits: string[] = [`Special: ${s.selectedSpecialSection}`];
    if (s.selectedRow) bits.push(`Row ${s.selectedRow}`);
    if (s.selectedSeat) bits.push(`Seat ${s.selectedSeat}`);
    seatContext = bits.join(" · ");
  } else if (s.showSuites && s.selectedSuite) {
    const name = getSuiteName(s.selectedSuite);
    seatContext = name ? `Suite: ${name}` : "Suite";
  } else {
    const bits: string[] = [];
    if (s.selectedSection) bits.push(`Sec ${s.selectedSection}`);
    if (s.selectedRow) bits.push(`Row ${s.selectedRow}`);
    if (s.selectedSeat) bits.push(`Seat ${s.selectedSeat}`);
    if (bits.length) {
      seatContext = bits.join(" · ");
    }
  }

  return {
    pageHost: pageHost || undefined,
    gameContext,
    seatContext,
  };
}
